import type { Cents, TaxRate } from "../money.js";
import type { PaymentMethod } from "./payment.js";
import type {
  OrderId,
  OrderLineId,
  MenuItemId,
  ModifierId,
  PaymentId,
  TableId,
  UserId,
  VenueId,
} from "../ids.js";

export type OrderStatus = "open" | "sent" | "paid" | "void";
export type OrderLineStatus = "active" | "voided";

/**
 * Snapshot eines gewählten Modifiers auf der Bestellzeile. Bewusst eine Kopie aus der
 * Speisekarte — friert Name und Preis zum Bestellzeitpunkt ein (Beleg-Treue).
 */
export interface OrderLineModifier {
  modifierId: ModifierId;
  name: string;
  priceDeltaCents: Cents;
}

export interface OrderLine {
  id: OrderLineId;
  menuItemId: MenuItemId;
  /** Snapshot des Artikelnamens zum Bestellzeitpunkt. */
  name: string;
  qty: number;
  /** Snapshot des Einzelpreises (ohne Modifier) zum Bestellzeitpunkt. */
  unitPriceCents: Cents;
  /** Snapshot des Steuersatzes. */
  taxRate: TaxRate;
  modifiers: OrderLineModifier[];
  /** Freitext für die Küche, z. B. "gut durch". */
  note?: string;
  /** Gang (course) für die Küchen-Reihenfolge, z. B. 1 = Vorspeise. */
  course?: number;
  status: OrderLineStatus;
  /**
   * Snapshot des MenuItem.isInfo-Flags zum Bestellzeitpunkt. True = reine Stations-
   * Meldung (Pseudo-Artikel), erscheint nicht auf dem Kundenbeleg und nicht in den
   * Verkaufs-Auswertungen.
   */
  isInfo?: boolean;
}

/** Eine (Teil-)Zahlung = ein Beleg. Bezieht sich auf bezahlte Mengen je Zeile. */
export interface OrderPayment {
  paymentId: PaymentId;
  /** Welche Mengen welcher Zeilen mit diesem Beleg bezahlt wurden. */
  items: { lineId: OrderLineId; qty: number }[];
  amountCents: Cents;
  tipCents: Cents;
  method: PaymentMethod;
  atMs: number;
  /** Vom Server vergebene Belegnummer. */
  belegNo?: number;
}

/** Küchen-Freigabe einer Position (mit Zeitstempel für die Wartezeit). */
export interface ServedEntry {
  lineId: OrderLineId;
  atMs: number;
}

export interface Order {
  id: OrderId;
  venueId: VenueId;
  tableRef: TableId;
  waiterId: UserId;
  status: OrderStatus;
  openedAtMs: number;
  /** Vom Server vergebene fortlaufende Bon-/Sequenznummer (für Beleg & Küche). */
  seqNo?: number;
  lines: OrderLine[];
  /** Erfasste (Teil-)Zahlungen / Belege. */
  payments?: OrderPayment[];
  /** Bereits in der Küche freigegebene Positionen. */
  served?: ServedEntry[];
  /** Vor dem Kassieren an die Stationen gesendete Positionen (lines.sent). */
  sentLines?: ServedEntry[];
}

/** Ist diese Position schon (küchenseitig) freigegeben? */
export function isLineServed(o: { served?: ServedEntry[] }, lineId: OrderLineId): boolean {
  return (o.served ?? []).some((s) => s.lineId === lineId);
}

/** Zeitpunkt, ab dem eine Position "in der Küche" ist = Zeit der Zahlung, die sie enthält. */
export function paidAtOf(o: { payments?: OrderPayment[] }, lineId: OrderLineId): number | null {
  for (const p of o.payments ?? []) if (p.items.some((i) => i.lineId === lineId)) return p.atMs;
  return null;
}

/** Wurde diese Position vor dem Kassieren an die Stationen gesendet? */
export function isLineSent(o: { sentLines?: ServedEntry[] }, lineId: OrderLineId): boolean {
  return (o.sentLines ?? []).some((s) => s.lineId === lineId);
}

/**
 * Ab wann ist eine Position an der Station? Gesendet → Sendezeit, sonst Zahlungszeit.
 * Basis für Wartezeiten und die Sortierung am Küchen-Screen.
 */
export function stationAtOf(
  o: { payments?: OrderPayment[]; sentLines?: ServedEntry[] },
  lineId: OrderLineId,
): number | null {
  const sent = (o.sentLines ?? []).find((s) => s.lineId === lineId);
  return sent ? sent.atMs : paidAtOf(o, lineId);
}

/** Minimaler Typ für die Abrechnungs-Helfer (Order ODER Draft mit Zeilen+Zahlungen). */
type Payable = { lines: OrderLine[]; payments?: OrderPayment[] };

/** Bereits bezahlte Menge einer Zeile (über alle Belege summiert). */
export function paidQtyOf(o: Payable, lineId: OrderLineId): number {
  return (o.payments ?? []).reduce(
    (s, p) => s + p.items.filter((i) => i.lineId === lineId).reduce((a, i) => a + i.qty, 0),
    0,
  );
}

/** Noch offene (zu zahlende) Menge einer Zeile. Stornierte Zeilen: 0. */
export function lineRemainingQty(o: Payable, line: OrderLine): number {
  if (line.status === "voided") return 0;
  return Math.max(0, line.qty - paidQtyOf(o, line.id));
}

function perUnitCents(line: OrderLine): number {
  return line.unitPriceCents + line.modifiers.reduce((s, m) => s + m.priceDeltaCents, 0);
}

/** Noch offener Betrag (Summe der unbezahlten Mengen). */
export function orderRemainingCents(o: Payable): Cents {
  return o.lines.reduce((s, l) => s + perUnitCents(l) * lineRemainingQty(o, l), 0) as Cents;
}

/** Bereits kassierter Betrag (Summe aller Belege, ohne Trinkgeld). */
export function orderPaidCents(o: Payable): Cents {
  return (o.payments ?? []).reduce((s, p) => s + p.amountCents, 0) as Cents;
}

/** Vollständig bezahlt? (mind. eine aktive Zeile, alle Mengen abgedeckt). */
export function isFullyPaid(o: Payable): boolean {
  const active = o.lines.filter((l) => l.status !== "voided");
  return active.length > 0 && active.every((l) => lineRemainingQty(o, l) === 0);
}

/** Preis einer einzelnen Zeile inkl. Modifier und Menge. */
export function lineTotalCents(line: OrderLine): Cents {
  if (line.status === "voided") return 0 as Cents;
  const perUnit =
    line.unitPriceCents + line.modifiers.reduce((s, m) => s + m.priceDeltaCents, 0);
  return Math.round(perUnit * line.qty) as Cents;
}

/** Gesamtsumme einer Bestellung (nur aktive Zeilen). */
export function orderTotalCents(order: Order): Cents {
  return order.lines.reduce((s, l) => s + lineTotalCents(l), 0) as Cents;
}
